import * as Yup from 'yup';
import { latinUmlautNumbersSymbolRegex } from 'src/helpers/regexHelpers';
import { emailPrefixCutter } from 'src/helpers/formHelpers';
import { ChallengeNames, Values } from './LoginPage';
import { UserShape } from 'src/interfaces';
import { putUser, setUserRole, tempSaveUser } from 'src/redux/actions/authActions';
import { setAuthToken } from 'src/helpers';
import { PAGES } from 'src/constants/pages';
import { Dispatch } from 'redux';

import { getCompanyWorkspacesHelper } from 'src/redux/sagas/sagaHelpers/workspacesApiHelpers';
import { ACCOUNT_ROLES, ACCOUNT_TYPE_SERVER_REQUEST } from 'src/constants/globalConstants';
import { getCurrentWorkspaceFromLocalStorage } from 'src/utils/comon';

export const validationSchema = Yup.object({
  email: Yup.string()
    .email('Wrong format. Please, enter the email as “your@email.com”')
    .required('Email cannot be blank'),
  password: Yup.string()
    .required('Password can’t be blank')
    .matches(latinUmlautNumbersSymbolRegex, 'Please, use only Latin')
    .min(8, 'Need at least 8 chars long')
    .matches(/[a-z]/, 'Need at least 1 lowercase')
    .matches(/[A-Z]/, 'Need at least 1 uppercase')
    .matches(/[0-9]/, 'Need at least 1 digit')
    .matches(/^.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?].*$/, 'Need at least 1 symbol character.')
});

type ValidateErrors = { email?: string; password?: string };

export const validate = (values: Values): ValidateErrors => {
  const errors: { email?: string; password?: string } = {};
  const emailPrefix = emailPrefixCutter(values.email);

  if (emailPrefix && values.password.includes(emailPrefix)) {
    errors.password = 'The password must not contain the part of login.';
  }

  return errors;
};

export const initialValues: Values = {
  email: '',
  password: ''
};

export const defaultReqError = 'One or more fields have an error, please check again.';

export const authHandler = (user: UserShape, dispatch: Dispatch<any>, push: (path: string) => void): void => {
  if (
    user?.challengeName === ChallengeNames.forceChangePassword ||
    user?.challengeName === ChallengeNames.newPasswordRequired
  ) {
    dispatch(putUser(user));
    return push(PAGES.UPDATE_PASSWORD);
  }

  setAuthToken(user.signInUserSession.accessToken.jwtToken);
  dispatch(putUser(user));
  dispatch(
    setUserRole(
      user.signInUserSession.accessToken.payload['cognito:groups']
        ?.filter((item) => item.startsWith('Role:'))
        .map((item) => item.replace('Role:', ''))
        .join(', ') as ACCOUNT_ROLES
    )
  );
};

export const redirectHandler = async (user: UserShape, push: (path: string) => void): Promise<void> => {
  const cognitoGroups = user.signInUserSession.accessToken.payload['cognito:groups'] || [];
  const isCompanyRole = cognitoGroups.includes(ACCOUNT_TYPE_SERVER_REQUEST[ACCOUNT_ROLES.COMPANY]);
  const isUserRole = cognitoGroups.includes(ACCOUNT_TYPE_SERVER_REQUEST[ACCOUNT_ROLES.USER]);
  const isMultiuserRole = cognitoGroups.includes(ACCOUNT_TYPE_SERVER_REQUEST[ACCOUNT_ROLES.MULTIUSER]);

  if (isMultiuserRole) {
    push(PAGES.CHOOSE_ACCOUNT_TYPE);
    return;
  }

  if (isCompanyRole) {
    try {
      const response = await getCompanyWorkspacesHelper();
      const freshWorkspaces = response?.data?.data || [];

      if (freshWorkspaces && Array.isArray(freshWorkspaces) && freshWorkspaces.length > 0) {
        push(PAGES.WORKSPACES_MANAGEMENT);
      } else {
        push(PAGES.WORKSPACES_CREATE);
      }
    } catch (error) {
      console.error('Error fetching workspaces:', error);
      push(PAGES.WORKSPACES_CREATE);
    }
    return;
  }

  if (isUserRole) {
    const savedWorkspase = getCurrentWorkspaceFromLocalStorage(user?.attributes?.email);

    savedWorkspase ? push(PAGES.DASHBOARD) : push(PAGES.WORKSPACES_CHOOSE);

    return;
  }

  push(PAGES.HOME);
};
